import { Controller } from "@hotwired/stimulus";

/**
 * GO4TEACH lot 20 — « La salle des profs »: the full-screen viewer of the home.
 *
 * Every story is rendered by the server as a hidden slide (its card, its reactions, its actions);
 * this controller only walks them: about 5 seconds a card, a tap on the right third (or →) for the
 * next, on the left third (or ←) for the previous, a hold pauses, Escape closes. A colleague's
 * story is marked seen once (POST …/view); a reaction is one of three, tapped again to remove it.
 * The author's shares are counted by channel — the image (read from this origin) shared through the
 * Web Share API when the browser can, else downloaded; a failure is said, never swallowed.
 */
const DURATION = 5000;
const TAP = 250;

export default class extends Controller {
  static targets = ["viewer", "slide", "fill", "reaction", "pending", "shareError"];
  static values = { viewUrl: String, reactionUrl: String, sharesUrl: String, openStory: String };

  connect() {
    this.index = -1;
    this.paused = false;
    if (this.openStoryValue) {
      const at = this.slideTargets.findIndex((slide) => slide.dataset.storyId === this.openStoryValue);
      if (at >= 0) this.show(at);
      this.waitForImage();
    }
  }

  disconnect() {
    this.stopTimer();
    clearTimeout(this.reloadTimer);
  }

  open(event) {
    const at = parseInt(event.currentTarget.dataset.index, 10);
    if (!Number.isNaN(at)) this.show(at);
  }

  show(at) {
    if (at < 0 || at >= this.slideTargets.length) return this.close();

    this.slideTargets.forEach((slide, i) => { slide.hidden = i !== at; });
    this.index = at;
    this.viewerTarget.hidden = false;
    document.body.classList.add("ds-modal-open");
    this.viewerTarget.focus({ preventScroll: true });
    this.markViewed(this.slideTargets[at]);
    this.paused = false;
    this.elapsed = 0;
    this.startTimer();
  }

  close() {
    this.stopTimer();
    this.viewerTarget.hidden = true;
    document.body.classList.remove("ds-modal-open");
    this.index = -1;
  }

  next() { this.show(this.index + 1); }

  previous() { this.show(Math.max(this.index - 1, 0)); }

  key(event) {
    if (event.key === "Escape") this.close();
    else if (event.key === "ArrowRight") this.next();
    else if (event.key === "ArrowLeft") this.previous();
  }

  // A press pauses; a short one is a tap: left third back, else forward.
  hold(event) {
    this.pressedAt = Date.now();
    this.pressX = event.clientX;
    this.pressRect = event.currentTarget.getBoundingClientRect();
    this.pause();
  }

  release() {
    if (this.pressedAt === undefined) return;
    const tap = Date.now() - this.pressedAt < TAP;
    this.pressedAt = undefined;
    if (!tap) return this.resume();
    if (this.pressX - this.pressRect.left < this.pressRect.width / 3) this.previous();
    else this.next();
  }

  toggleReport(event) {
    if (event.currentTarget.open) this.pause();
    else this.resume();
  }

  pause() {
    if (this.paused) return;
    this.paused = true;
    this.elapsed += Date.now() - this.startedAt;
    this.stopTimer();
  }

  resume() {
    if (!this.paused || this.index < 0) return;
    this.paused = false;
    this.startTimer();
  }

  startTimer() {
    this.stopTimer();
    this.startedAt = Date.now();
    const fill = this.currentFill();
    const tick = () => {
      const done = Math.min((this.elapsed + Date.now() - this.startedAt) / DURATION, 1);
      if (fill) fill.style.width = `${done * 100}%`;
      if (done >= 1) return this.next();
      this.frame = requestAnimationFrame(tick);
    };
    this.frame = requestAnimationFrame(tick);
  }

  stopTimer() {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = null;
  }

  currentFill() {
    const slide = this.slideTargets[this.index];
    return slide && this.fillTargets.find((fill) => slide.contains(fill));
  }

  markViewed(slide) {
    if (slide.dataset.viewed === "true" || slide.dataset.mine === "true") return;
    slide.dataset.viewed = "true";
    this.request("POST", this.url(this.viewUrlValue, slide.dataset.storyId));
  }

  async react(event) {
    const button = event.currentTarget;
    const slide = button.closest("[data-story-id]");
    const kind = button.getAttribute("aria-pressed") === "true" ? "" : button.dataset.kind;
    const response = await this.request("PUT", this.url(this.reactionUrlValue, slide.dataset.storyId), { kind });
    if (!response || !response.ok) return;

    const body = await response.json();
    this.reactionTargets.filter((target) => slide.contains(target)).forEach((target) => {
      const pressed = target.dataset.kind === body.my_reaction;
      target.setAttribute("aria-pressed", pressed);
      target.classList.toggle("ds-btn--primary", pressed);
      target.classList.toggle("ds-btn--outline", !pressed);
      target.style.background = pressed ? "" : "rgba(255,255,255,.92)";
      const count = target.querySelector("[data-count]");
      if (count) count.textContent = body.reactions[target.dataset.kind];
    });
  }

  // A share launched (a link followed): counted, never verified.
  shared(event) {
    const { storyId, channel } = event.currentTarget.dataset;
    this.countShare(storyId, channel);
  }

  async shareImage(event) {
    const { storyId, imageUrl, shareText } = event.currentTarget.dataset;
    const slide = event.currentTarget.closest("[data-story-id]");
    const error = this.shareErrorTargets.find((target) => slide && slide.contains(target));
    if (error) error.hidden = true;
    this.pause();
    try {
      const response = await fetch(imageUrl, { credentials: "same-origin" });
      if (!response.ok) throw new Error(`image ${response.status}`);
      const blob = await response.blob();
      const file = new File([blob], "go4teach.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: shareText });
        this.countShare(storyId, "other");
      } else {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "go4teach.png";
        link.click();
        URL.revokeObjectURL(link.href);
        this.countShare(storyId, "download");
      }
    } catch (failure) {
      // The share sheet closed without sharing: nothing to count, nothing to say.
      if (failure && failure.name === "AbortError") return;
      if (error) error.hidden = false;
    }
  }

  countShare(storyId, channel) {
    this.request("POST", this.url(this.sharesUrlValue, storyId), { channel });
  }

  // Right after a share (`?shared=<id>`): the image of THAT story is rendered in the background —
  // the page is read again a few times while it is still « en préparation ».
  waitForImage() {
    const slide = this.slideTargets.find((target) => target.dataset.storyId === this.openStoryValue);
    if (!slide || !this.pendingTargets.some((target) => slide.contains(target))) return;
    const url = new URL(window.location.href);
    const tries = parseInt(url.searchParams.get("wait") || "0", 10);
    if (tries >= 10) return;
    url.searchParams.set("wait", tries + 1);
    this.reloadTimer = setTimeout(() => window.location.replace(url.toString()), 3000);
  }

  url(template, id) { return template.replace("__ID__", encodeURIComponent(id)); }

  request(method, url, body) {
    const token = document.querySelector('meta[name="csrf-token"]');
    return fetch(url, {
      method,
      credentials: "same-origin",
      keepalive: true,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": token ? token.content : "",
      },
      body: body ? JSON.stringify(body) : undefined,
    }).catch(() => null);
  }
}
